import React from 'react';
import { useImmerReducer } from 'use-immer';
import MultiRef from 'react-multi-ref';
import initialStates, { RADIOS_INITIAL_VALUES } from '../lib/buildInitialState';
import {
	Box,
	Flex,
	HStack,
	Switch,
	Input,
	Text,
	Textarea,
	Button,
	Accordion,
	CloseButton,
	Portal,
	Dialog
} from '@chakra-ui/react';
import theme from '../theme';

function reducer(draft, action) {
	switch (action.type) {
		case 'initialize_fields': {
			draft.fields = initialStates.delta_p_list;
			break;
		}
		case 'update_field_value': {
			const { section, field, value, key } = action.payload;

			if (section && key) {
				draft.fields[section][field] = {
					...draft.fields[section][field],
					...RADIOS_INITIAL_VALUES,
					[key]: value
				};
			} else if (section) {
				draft.fields[section][field] = value;
			} else {
				draft.fields[field] = value;
			}

			break;
		}
		case 'add_designated_facility_personnel': {
			draft.fields.dive_planning.designated_facility_personnel.push({
				name: draft.fields.dive_planning.designated_facility_contact_name,
				phone_number_contact_method: draft.fields.dive_planning.designated_facility_phone_number_contact_method
			});

			draft.fields.dive_planning.designated_facility_contact_name = '';
			draft.fields.dive_planning.designated_facility_phone_number_contact_method = '';

			break;
		}
		case 'remove_designated_facility_personnel': {
			const { index } = action.payload;
			draft.fields.dive_planning.designated_facility_personnel.splice(index, 1);
			break;
		}
		case 'add_direct_authority_personnel': {
			draft.fields.dive_planning.direct_authority_personnel.push({
				name: draft.fields.dive_planning.direct_authority_onsite_name,
				phone_number_contact_method:
					draft.fields.dive_planning.direct_authority_onsite_phone_number_contact_method
			});

			draft.fields.dive_planning.direct_authority_onsite_name = '';
			draft.fields.dive_planning.direct_authority_onsite_phone_number_contact_method = '';

			break;
		}
		case 'remove_direct_authority_personnel': {
			const { index } = action.payload;
			draft.fields.dive_planning.direct_authority_personnel.splice(index, 1);
			break;
		}
		default:
			break;
	}
}

export default ({ formData, saveForm, currentPacket, currentForm, edit }) => {
	const [state, dispatch] = useImmerReducer(reducer, { fields: formData });

	const [removeDesignatedFacilityDialogOpen, setRemoveDesignatedFacilityDialogOpen] = React.useState(null);
	const [removeDirectAuthorityDialogOpen, setRemoveDirectAuthorityDialogOpen] = React.useState(null);

	const [designatedFacilityPersonnelRefs] = React.useState(() => new MultiRef());
	const [directAuthorityPersonnelRefs] = React.useState(() => new MultiRef());

	React.useEffect(() => {
		if (Array.isArray(state.fields)) {
			dispatch({ type: 'initialize_fields' });
		}
	}, []);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	const reqsMet = React.useMemo(
		() =>
			state.fields?.mitigation_plan?.delta_p_hazards_site_representatives &&
			state.fields?.mitigation_plan?.lockout_tagout_plan &&
			state.fields?.mitigation_plan?.plan_discussed,
		[
			state.fields?.mitigation_plan?.delta_p_hazards_site_representatives,
			state.fields?.mitigation_plan?.lockout_tagout_plan,
			state.fields?.mitigation_plan?.plan_discussed
		]
	);

	return (
		<>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Dive Mode
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.scope_of_work?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'scope_of_work',
																key: 'yes',
																value: !state.fields?.dive_planning?.scope_of_work?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.scope_of_work?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'scope_of_work',
																key: 'no',
																value: !state.fields?.dive_planning?.scope_of_work?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Does the scope of work consider any potential differential pressure (“Delta-P”)
										hazards?
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Potential Hazards
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields?.dive_planning?.potential_hazards}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_planning',
													field: 'potential_hazards',
													value: target.value
												}
											});
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.site_personnel?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'site_personnel',
																key: 'yes',
																value: !state.fields?.dive_planning?.site_personnel?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.site_personnel?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'site_personnel',
																key: 'no',
																value: !state.fields?.dive_planning?.site_personnel?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Have all the site personnel been notified of the exact date and time ranges of
										diving operations?
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack flex={1} p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.designated_facility_contact?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'designated_facility_contact',
																key: 'yes',
																value: !state.fields?.dive_planning
																	?.designated_facility_contact?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.designated_facility_contact?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'designated_facility_contact',
																key: 'no',
																value: !state.fields?.dive_planning
																	?.designated_facility_contact?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Is there a designated facility person present for normal and emergency contacts?
									</Text>
								</HStack>
							</Flex>
							<Flex direction="column" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
								<Flex>
									<Text fontSize={16} w="100%" as="label" p="8px">
										Name (list in priority order)
									</Text>
									<Text fontSize={16} w="100%" as="label" p="8px">
										Phone Number/Contact Method
									</Text>
								</Flex>
								{state.fields?.dive_planning?.designated_facility_personnel?.map((personnel, index) => (
									<Flex key={index} align="center">
										<Text fontSize={16} w="100%" p="8px">
											{personnel.name}
										</Text>
										<Flex w="100%" alignItems="center">
											<Text fontSize={16} w="100%" p="8px">
												{personnel.phone_number_contact_method}
											</Text>
											{currentPacket.packet_tag === 'supervisor' && (
												<>
													<CloseButton
														size="md"
														m="8px"
														color={theme.colors.white}
														bgColor={theme.colors.red[500]}
														_hover={{ background: theme.colors.red[600] }}
														onClick={() => setRemoveDesignatedFacilityDialogOpen(index)}
													/>
													<Dialog.Root
														role="alertdialog"
														initialFocusEl={designatedFacilityPersonnelRefs.map.get(index)}
														onOpenChange={({ open }) => {
															if (!open) {
																setRemoveDesignatedFacilityDialogOpen(null);
															}
														}}
														open={removeDesignatedFacilityDialogOpen === index}
														placement="center"
													>
														<Portal>
															<Dialog.Backdrop />
															<Dialog.Positioner>
																<Dialog.Content opacity="1 !important">
																	<Dialog.Header>
																		<Dialog.Title fontSize="lg" fontWeight="bold">
																			Confirm Delete
																		</Dialog.Title>
																	</Dialog.Header>
																	<Dialog.Body>
																		<p>
																			Are you sure you want to remove this item?
																		</p>
																	</Dialog.Body>
																	<Dialog.Footer>
																		<Dialog.ActionTrigger asChild>
																			<Button
																				size="lg"
																				ref={designatedFacilityPersonnelRefs.ref(
																					index
																				)}
																				onClick={() =>
																					setRemoveDesignatedFacilityDialogOpen(
																						null
																					)
																				}
																			>
																				Cancel
																			</Button>
																		</Dialog.ActionTrigger>
																		<Button
																			colorPalette="red"
																			size="lg"
																			ml={3}
																			bg={theme.colors.red[500]}
																			_hover={{
																				background: theme.colors.red[600]
																			}}
																			color={theme.colors.white}
																			onClick={() => {
																				setRemoveDesignatedFacilityDialogOpen(
																					null
																				);
																				dispatch({
																					type: 'remove_designated_facility_personnel',
																					payload: { index }
																				});
																			}}
																		>
																			Delete Item
																		</Button>
																	</Dialog.Footer>
																	<Dialog.CloseTrigger asChild>
																		<CloseButton size="sm" />
																	</Dialog.CloseTrigger>
																</Dialog.Content>
															</Dialog.Positioner>
														</Portal>
													</Dialog.Root>
												</>
											)}
										</Flex>
									</Flex>
								))}
							</Flex>
							<Flex align="flex-end" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
								<Flex w="100%" p="8px" flexDirection="column" justifyContent="space-between">
									<Text fontSize={16} as="label" pb="4px">
										Name (list in priority order)
									</Text>
									<Input
										bgColor={theme.colors.white}
										value={state.fields?.dive_planning?.designated_facility_contact_name}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_planning',
													field: 'designated_facility_contact_name',
													value: target.value
												}
											});
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Flex>
								<Flex w="100%" alignItems="flex-end">
									<Flex w="100%" p="8px" flexDirection="column">
										<Text fontSize={16} as="label" pb="4px">
											Phone Number/Contact Method
										</Text>
										<Input
											bgColor={theme.colors.white}
											value={
												state.fields?.dive_planning
													?.designated_facility_phone_number_contact_method
											}
											onChange={({ target }) => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'dive_planning',
														field: 'designated_facility_phone_number_contact_method',
														value: target.value
													}
												});
											}}
											disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Flex>
									<Button
										size="lg"
										m="8px"
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => {
											if (
												state.fields?.dive_planning?.designated_facility_contact_name &&
												state.fields?.dive_planning
													?.designated_facility_phone_number_contact_method
											) {
												dispatch({ type: 'add_designated_facility_personnel' });
											}
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										Add
									</Button>
								</Flex>
							</Flex>
							<Flex>
								<HStack flex={1} p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.direct_authority_onsite?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'direct_authority_onsite',
																key: 'yes',
																value: !state.fields?.dive_planning
																	?.direct_authority_onsite?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.dive_planning?.direct_authority_onsite?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'dive_planning',
																field: 'direct_authority_onsite',
																key: 'no',
																value: !state.fields?.dive_planning
																	?.direct_authority_onsite?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Can a facility person with <Text as="u">direct authority</Text> be onsite at all
										times?
									</Text>
								</HStack>
							</Flex>
							<Flex direction="column" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
								<Flex>
									<Text fontSize={16} w="100%" as="label" p="8px">
										Name (list in priority order)
									</Text>
									<Text fontSize={16} w="100%" as="label" p="8px">
										Phone Number/Contact Method
									</Text>
								</Flex>
								{state.fields?.dive_planning?.direct_authority_personnel?.map((personnel, index) => (
									<Flex key={index} align="center">
										<Text fontSize={16} w="100%" p="8px">
											{personnel.name}
										</Text>
										<Flex w="100%" alignItems="center">
											<Text fontSize={16} w="100%" p="8px">
												{personnel.phone_number_contact_method}
											</Text>
											{currentPacket.packet_tag === 'supervisor' && (
												<>
													<CloseButton
														size="md"
														m="8px"
														color={theme.colors.white}
														bgColor={theme.colors.red[500]}
														_hover={{ background: theme.colors.red[600] }}
														onClick={() => setRemoveDirectAuthorityDialogOpen(index)}
													/>
													<Dialog.Root
														role="alertdialog"
														initialFocusEl={directAuthorityPersonnelRefs.map.get(index)}
														onOpenChange={({ open }) => {
															if (!open) {
																setRemoveDirectAuthorityDialogOpen(null);
															}
														}}
														open={removeDirectAuthorityDialogOpen === index}
														placement="center"
													>
														<Portal>
															<Dialog.Backdrop />
															<Dialog.Positioner>
																<Dialog.Content opacity="1 !important">
																	<Dialog.Header>
																		<Dialog.Title fontSize="lg" fontWeight="bold">
																			Confirm Delete
																		</Dialog.Title>
																	</Dialog.Header>
																	<Dialog.Body>
																		<p>
																			Are you sure you want to remove this item?
																		</p>
																	</Dialog.Body>
																	<Dialog.Footer>
																		<Dialog.ActionTrigger asChild>
																			<Button
																				size="lg"
																				ref={directAuthorityPersonnelRefs.ref(
																					index
																				)}
																				onClick={() =>
																					setRemoveDirectAuthorityDialogOpen(
																						null
																					)
																				}
																			>
																				Cancel
																			</Button>
																		</Dialog.ActionTrigger>
																		<Button
																			colorPalette="red"
																			size="lg"
																			ml={3}
																			bg={theme.colors.red[500]}
																			_hover={{
																				background: theme.colors.red[600]
																			}}
																			color={theme.colors.white}
																			onClick={() => {
																				setRemoveDirectAuthorityDialogOpen(
																					null
																				);
																				dispatch({
																					type: 'remove_direct_authority_personnel',
																					payload: { index }
																				});
																			}}
																		>
																			Delete Item
																		</Button>
																	</Dialog.Footer>
																	<Dialog.CloseTrigger asChild>
																		<CloseButton size="sm" />
																	</Dialog.CloseTrigger>
																</Dialog.Content>
															</Dialog.Positioner>
														</Portal>
													</Dialog.Root>
												</>
											)}
										</Flex>
									</Flex>
								))}
							</Flex>
							<Flex align="flex-end" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
								<Flex w="100%" p="8px" flexDirection="column" justifyContent="space-between">
									<Text fontSize={16} as="label" pb="4px">
										Name (list in priority order)
									</Text>
									<Input
										bgColor={theme.colors.white}
										value={state.fields?.dive_planning?.direct_authority_onsite_name}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'dive_planning',
													field: 'direct_authority_onsite_name',
													value: target.value
												}
											});
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Flex>
								<Flex w="100%" alignItems="flex-end">
									<Flex w="100%" p="8px" flexDirection="column">
										<Text fontSize={16} as="label" pb="4px">
											Phone Number/Contact Method
										</Text>
										<Input
											bgColor={theme.colors.white}
											value={
												state.fields?.dive_planning
													?.direct_authority_onsite_phone_number_contact_method
											}
											onChange={({ target }) => {
												dispatch({
													type: 'update_field_value',
													payload: {
														section: 'dive_planning',
														field: 'direct_authority_onsite_phone_number_contact_method',
														value: target.value
													}
												});
											}}
											disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Flex>
									<Button
										size="lg"
										m="8px"
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => {
											if (
												state.fields?.dive_planning?.direct_authority_onsite_name &&
												state.fields?.dive_planning
													?.direct_authority_onsite_phone_number_contact_method
											) {
												dispatch({ type: 'add_direct_authority_personnel' });
											}
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										Add
									</Button>
								</Flex>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Mitigation Plan
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'review_facility',
													value: !state.fields?.mitigation_plan?.review_facility
												}
											})
										}
										checked={state.fields?.mitigation_plan?.review_facility}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Review the plans, blueprints, photos, surveys, and/or as-built drawings to
										understand the lay-out and operation of the facility.
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Locate and document all Delta-P hazards along with site representatives.
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields?.mitigation_plan?.delta_p_hazards_site_representatives}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'delta_p_hazards_site_representatives',
													value: target.value
												}
											});

											if (!target.value && state.fields?.complete) {
												dispatch({
													type: 'update_field_value',
													payload: {
														field: 'complete',
														value: false
													}
												});
											}
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckdChange={() => {
											if (
												state.fields?.mitigation_plan?.lockout_tagout_plan &&
												state.fields?.complete
											) {
												dispatch({
													type: 'update_field_value',
													payload: {
														field: 'complete',
														value: false
													}
												});
											}

											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'lockout_tagout_plan',
													value: !state.fields?.mitigation_plan?.lockout_tagout_plan
												}
											});
										}}
										checked={state.fields?.mitigation_plan?.lockout_tagout_plan}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Create a site-specific lockout/tagout plan with the facility.
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Identify emergency procedures for diver recovery.
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields?.mitigation_plan?.emergency_procedures}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'emergency_procedures',
													value: target.value
												}
											});
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Text fontSize={16} as="label" pb="4px">
										Identify emergency rescue/recovery assets.
									</Text>
									<Textarea
										fontSize={16}
										bgColor={theme.colors.white}
										value={state.fields?.mitigation_plan?.emergency_assets}
										onChange={({ target }) => {
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'emergency_assets',
													value: target.value
												}
											});
										}}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
										_disabled={{
											opacity: 1,
											bgColor: theme.colors.gray[400],
											color: theme.colors.black
										}}
									/>
								</Box>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'capture_zone_min_safe_distance',
													value: !state.fields?.mitigation_plan
														?.capture_zone_min_safe_distance
												}
											})
										}
										checked={state.fields?.mitigation_plan?.capture_zone_min_safe_distance}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Identify the capture zone of the known hazards and calculate the minimum safe
										distance to be maintained from those areas by the diver and the diver’s
										umbilical.
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'diver_location_minimize_exposure',
													value: !state.fields?.mitigation_plan
														?.diver_location_minimize_exposure
												}
											})
										}
										checked={state.fields?.mitigation_plan?.diver_location_minimize_exposure}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Identify the best location to tend the diver to minimize exposure.
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack flex={1} p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.mitigation_plan?.physical_barriers?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'mitigation_plan',
																field: 'physical_barriers',
																key: 'yes',
																value: !state.fields?.mitigation_plan?.physical_barriers
																	?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.mitigation_plan?.physical_barriers?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'mitigation_plan',
																field: 'physical_barriers',
																key: 'no',
																value: !state.fields?.mitigation_plan?.physical_barriers
																	?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Can physical barriers be installed to eliminate Delta-P risk?
									</Text>
								</HStack>
							</Flex>
							<Flex ml="60px">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'physical_barriers_plan',
													value: !state.fields?.mitigation_plan?.physical_barriers_plan
												}
											})
										}
										checked={state.fields?.mitigation_plan?.physical_barriers_plan}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Create a plan to install physical barriers.
									</Text>
								</HStack>
							</Flex>
							<Flex ml="60px">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() => {
											if (
												state.fields?.mitigation_plan?.plan_discussed &&
												state.fields?.complete
											) {
												dispatch({
													type: 'update_field_value',
													payload: {
														field: 'complete',
														value: false
													}
												});
											}

											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'plan_discussed',
													value: !state.fields?.mitigation_plan?.plan_discussed
												}
											});
										}}
										checked={state.fields?.mitigation_plan?.plan_discussed}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Discuss this plan with site representatives.
									</Text>
								</HStack>
							</Flex>
							<Flex>
								<HStack flex={1} p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Flex flexDirection="row" alignItems="center">
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.mitigation_plan?.systems_locations_confirmed?.yes
														? 'green'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'mitigation_plan',
																field: 'systems_locations_confirmed',
																key: 'yes',
																value: !state.fields?.mitigation_plan
																	?.systems_locations_confirmed?.yes
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													Y
												</Text>
											</Flex>
										</Flex>
										<Flex px="8px" justifyContent="center" alignItems="center">
											<Flex
												justifyContent="center"
												alignItems="center"
												w="24px"
												h="24px"
												borderWidth="2px"
												borderRadius="50%"
												borderColor={
													state.fields?.mitigation_plan?.systems_locations_confirmed?.no
														? 'red'
														: 'transparent'
												}
												_hover={{
													cursor:
														currentPacket.packet_tag !== 'supervisor' && !edit
															? 'default'
															: 'pointer'
												}}
												pointerEvents={
													currentPacket.packet_tag !== 'supervisor' && !edit ? 'none' : 'auto'
												}
												onClick={() => {
													if (currentPacket.packet_tag === 'supervisor' || edit) {
														dispatch({
															type: 'update_field_value',
															payload: {
																section: 'mitigation_plan',
																field: 'systems_locations_confirmed',
																key: 'no',
																value: !state.fields?.mitigation_plan
																	?.systems_locations_confirmed?.no
															}
														});
													}
												}}
											>
												<Text fontSize={18} w="17.5px" align="center">
													N
												</Text>
											</Flex>
										</Flex>
									</Flex>
									<Text fontSize={16} as="label" pl="4px">
										Have all locations of potential “Emergency Use Only” intakes, pumps, valves, and
										fire suppression systems been confirmed to not affect dive operations if put
										into service?
									</Text>
								</HStack>
							</Flex>
							<Flex ml="60px">
								<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
									<Switch.Root
										colorPalette="blue"
										size="lg"
										onCheckedChange={() =>
											dispatch({
												type: 'update_field_value',
												payload: {
													section: 'mitigation_plan',
													field: 'risks_plan_established',
													value: !state.fields?.mitigation_plan?.risks_plan_established
												}
											})
										}
										checked={state.fields?.mitigation_plan?.risks_plan_established}
										disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
									>
										<Switch.HiddenInput />
										<Switch.Control />
									</Switch.Root>
									<Text fontSize={16} as="label" pl="4px">
										Establish a plan with the facility to mitigate the Delta-P risks associated with
										the intake, pump, valve, and/or fire suppression system.
									</Text>
								</HStack>
							</Flex>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
			{reqsMet && (
				<Flex>
					<HStack w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Switch.Root
							colorPalette="blue"
							size="lg"
							disabled={currentPacket.packet_tag !== 'supervisor' && !edit}
							onCheckedChange={() => {
								dispatch({
									type: 'update_field_value',
									payload: {
										field: 'complete',
										value: !state.fields?.complete
									}
								});
							}}
							checked={state.fields?.complete}
						>
							<Switch.HiddenInput />
							<Switch.Control />
						</Switch.Root>
						<Text fontSize={16} as="label" pl="4px">
							Form Is Complete
						</Text>
					</HStack>
				</Flex>
			)}
		</>
	);
};
